// src/pages/Upload.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import useLocale from '../hooks/useLocale';
import axios from 'axios';
import CreatableSelect from 'react-select/creatable';
import './upload.css'; // Assuming upload.css or kebab-case: upload.css
import { formatBytes } from '../utils/formatters'; // Adjust the path ('../') as needed


const ALLOWED_MIME_TYPES = [
    'image/fits',
    'application/fits', // Sometimes used
    'image/x-fits',     // Another variation
    'image/jpeg',
    'image/png',
    'image/tiff'
    // Add/remove as needed
];

const ALLOWED_EXTENSIONS = [
    '.fits',
    '.fit',
    '.fz',    // Compressed FITS
    '.xisf',
    '.jpg',
    '.jpeg',
    '.png',
    '.tif',
    '.tiff'
    // Add/remove as needed
];

function Upload() {
    const { t } = useLocale(); // Get the t function
    const { user } = useAuth();

    // --- State ---
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [targetList, setTargetList] = useState([]);
    const [isLoadingTargets, setIsLoadingTargets] = useState(true);
    const [targetError, setTargetError] = useState(null);
    const [selectedTargetOption, setSelectedTargetOption] = useState(null);
    const [focalLength, setFocalLength] = useState('');
    const [exposureTime, setExposureTime] = useState('');
    const [filterUsed, setFilterUsed] = useState('');
    const [uploadProgress, setUploadProgress] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [overallProgress, setOverallProgress] = useState(0);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [totalBatchSize, setTotalBatchSize] = useState(0);
    const [currentUploadInstructions, setCurrentUploadInstructions] = useState([]);
    const [userQuota, setUserQuota] = useState({ used: null, allowed: null });
    const [quotaLoading, setQuotaLoading] = useState(true); // Initially loading
    const [quotaError, setQuotaError] = useState(null);
    const [isOverQuota, setIsOverQuota] = useState(false); // Will upload exceed quota?
    const [estimatedUsedQuota, setEstimatedUsedQuota] = useState(null); // Start as null
    const [message, setMessage] = useState(''); // State for general feedback/error messages

    // Helper function for basic placeholder replacement
    const formatString = useCallback((template, replacements = {}) => {
        if (!template) return '';
        let formatted = template;
        for (const key in replacements) {
            const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
            formatted = formatted.replace(regex, replacements[key]);
        }
        return formatted;
    }, []); // Empty dependency array - this function doesn't depend on component state/props

    // --- Fetch Existing Targets ---
    useEffect(() => {
        const fetchTargets = async () => {
            setIsLoadingTargets(true);
            setTargetError(null);
            console.log("[Upload] Fetching targets...");
            try {
                const token = localStorage.getItem('authToken');
                if (!token) {
                    setTargetError(t('upload_error_auth_token_missing', "Authentication token not found. Cannot load targets."));
                    setTargetList([]);
                    setIsLoadingTargets(false);
                    return;
                }
                const response = await fetch('/api/targets', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) {
                    let errorMsg = t('upload_error_target_fetch', `HTTP error! status: ${response.status}`);
                    try {
                        const errorData = await response.json();
                        // TODO: Use t(errorData.errorCode, errorMsg) when backend sends codes
                        errorMsg = errorData.message || errorMsg;
                    } catch (e) { /* Ignore JSON parsing error */ }
                    throw new Error(errorMsg);
                }
                const data = await response.json();
                console.log("[Upload] Targets received:", data.targets);
                setTargetList(Array.isArray(data.targets) ? data.targets : []);
            } catch (err) {
                console.error("Error fetching target list:", err);
                const errorCode = err.response?.data?.errorCode; // Get code
                const messageKey = errorCode || 'API_SERVER_ERROR_GENERIC';
                // Use t() to set the error state, maybe using the original message as fallback text
                setTargetError(t(messageKey, err.message || 'Could not load targets.'));
                setTargetList([]);
            } finally {
                setIsLoadingTargets(false);
            }
        };
        fetchTargets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t]); // Added t dependency as it's used in error handling

    //Effect to get quota
    useEffect(() => {
        const fetchQuota = async () => {
            if (!user) return; // Don't run if user isn't loaded

            setQuotaLoading(true);
            setQuotaError(null);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setQuotaError(t('error_auth_token_required', 'Authentication token required.'));
                setQuotaLoading(false);
                return;
            }

            try {
                const response = await fetch('/api/quota-status', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) {
                    let errorData = null; let errorCode = null;
                    try { errorData = await response.json(); errorCode = errorData?.errorCode; } catch (e) { }
                    throw new Error(t(errorCode || 'API_SERVER_ERROR_GENERIC', 'Failed to fetch quota status'));
                }
                const data = await response.json();
                setUserQuota({
                    used: data.used ?? 0, // Use nullish coalescing for safety
                    allowed: data.allowed ?? 0
                });
                setEstimatedUsedQuota(data.used ?? 0); // <<< Initialize estimate here
            } catch (err) {
                console.error("Error fetching quota:", err);
                setQuotaError(t('upload_error_fetch_quota', 'Could not load your storage quota information.')); // New key
            } finally {
                setQuotaLoading(false);
            }
        };

        fetchQuota();
        // Depend on user (to trigger fetch when user loads) and t (for error messages)
    }, [user, t]);

    //Effect for quota change
    // Add this useEffect in Upload.js
    useEffect(() => {
        if (userQuota.allowed !== null && userQuota.used !== null && totalBatchSize > 0) {
            const remainingQuota = userQuota.allowed - userQuota.used;
            if (totalBatchSize > remainingQuota) {
                setIsOverQuota(true);
                // Set a user-facing error message maybe? Or just rely on disabled button?
                // Example: setUploadErrorMessage(t('upload_error_quota_exceeded_batch', '...'));
                console.warn(`Quota check failed: Selected=${totalBatchSize}, Remaining=${remainingQuota}`);
            } else {
                setIsOverQuota(false);
            }
        } else {
            // Reset if no files selected or quota not loaded
            setIsOverQuota(false);
        }
        // Depend on the calculated batch size and the fetched quota info
    }, [totalBatchSize, userQuota]);

    const quotaDisplay = useMemo(() => {
        // Use estimatedUsedQuota if it's available (not null), otherwise use fetched userQuota.used
        const currentUsage = estimatedUsedQuota ?? userQuota.used;

        // Handle loading/error/null states first
        if (quotaLoading || quotaError || userQuota.allowed === null || currentUsage === null) {
            if (quotaLoading) return t('upload_loading_quota', 'Loading quota...');
            if (quotaError) return quotaError;
            return '';
        }

        const bytesAllowed = userQuota.allowed;
        const bytesUsed = currentUsage; // <<< Use the estimated value
        const decimals = bytesAllowed >= 1024 ** 3 ? 2 : 0;
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

        const i = bytesAllowed === 0 ? 0 : Math.floor(Math.log(bytesAllowed) / Math.log(k));
        const unitIndex = Math.min(i, sizes.length - 1);
        const targetUnit = sizes[unitIndex];
        const divisor = Math.pow(k, unitIndex);

        const formattedUsed = (bytesUsed / divisor).toFixed(decimals);
        const formattedAllowed = (bytesAllowed / divisor).toFixed(decimals);

        return `${t('upload_quota_label', 'Storage Used:')} ${formattedUsed} ${targetUnit} / ${formattedAllowed} ${targetUnit}`;

    }, [userQuota, quotaLoading, quotaError, estimatedUsedQuota, t]);

    // --- Handlers ---
    const handleFileChange = (event) => {
        setMessage(''); // Clear any previous message first
        setEstimatedUsedQuota(null); // Reset estimate
    
        const filesArray = Array.from(event.target.files);
    
        // --- START: File Type Validation ---
        const invalidFiles = filesArray.filter(file => {
            const extension = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase() : '';
            const mimeType = file.type ? file.type.toLowerCase() : ''; // Browser-reported MIME type
    
            const isAllowedMime = ALLOWED_MIME_TYPES.includes(mimeType);
            // For extension check, also allow if MIME type is unknown/generic but extension is valid
            const isAllowedExt = ALLOWED_EXTENSIONS.includes(extension);
            const isGenericMimeButAllowedExt = (!mimeType || mimeType === 'application/octet-stream') && isAllowedExt;
    
            // A file is considered VALID if its MIME type is explicitly allowed,
            // OR if its extension is allowed (especially important for types like FITS/XISF
            // where browser MIME type reporting might be unreliable or generic).
            const isValid = isAllowedMime || isAllowedExt || isGenericMimeButAllowedExt;
    
            // Return true for the filter if the file is INVALID
            return !isValid;
        });
    
        if (invalidFiles.length > 0) {
            // Invalid file(s) found! Show error, clear input, reset state.
            const invalidNames = invalidFiles.map(f => f.name).join(', ');
            // Use setMessage to display the error in the UI
            

            // --- UPDATED MESSAGE LOGIC ---
        // 1. Get the template string (which contains {{invalidNames}})
        const messageTemplate = t(
            'upload_error_invalid_file_type_selected', // The key for your locale file
            // Provide a basic fallback IN CASE the key is missing,
            // though the key itself should have the {{invalidNames}} placeholder
            `Invalid file type(s): ${invalidNames}. Allowed: FITS, XISF, JPG, PNG, TIFF.`
        );

        // 2. Use formatString to insert the actual names into the placeholder
        const formattedMessage = formatString(messageTemplate, { invalidNames: invalidNames });

        // 3. Set the final formatted message state
        setMessage(formattedMessage);
        // --- END UPDATED MESSAGE LOGIC ---




            // Clear the actual file input element
            event.target.value = null;
            // Reset component state related to selected files
            setSelectedFiles([]);
            setTotalBatchSize(0);
            setUploadProgress({});
            setOverallProgress(0);
            setCurrentFileIndex(0);
            setCurrentUploadInstructions([]);
            return; // Stop processing this file change event
        }
        // --- END: File Type Validation ---
    
        // --- If all files are valid, proceed as before ---
        setSelectedFiles(filesArray);
        setUploadProgress({}); // Clear old progress
        setOverallProgress(0);
        setCurrentFileIndex(0);
        setIsUploading(false); // Ensure not stuck in uploading state
        setCurrentUploadInstructions([]); // Clear previous instructions
        const totalSize = filesArray.reduce((acc, file) => acc + file.size, 0);
        setTotalBatchSize(totalSize);
        console.log(`[Upload] Selected ${filesArray.length} valid files, total size: ${totalSize} bytes`);
    };

    const handleTargetChange = (newValue) => {
        console.log("Target selected/created:", newValue);
        setSelectedTargetOption(newValue);
    };

    const getFileSize = useCallback((filename) => {
        const file = selectedFiles.find(f => f.name === filename);
        return file ? file.size : 0;
    }, [selectedFiles]);

    const handleUpload = async () => {

        setEstimatedUsedQuota(userQuota.used ?? 0); // Reset estimate to last known actual usage


        // --- Validation ---
        if (selectedFiles.length === 0) {
            alert(t('upload_error_no_files_selected', "Please select files to upload."));
            return;
        }
        if (!user || !user.username) {
            alert(t('upload_error_not_logged_in', "User information not available. Please log in again."));
            return;
        }
        const finalTargetName = selectedTargetOption ? selectedTargetOption.value.trim() : null;
        if (!finalTargetName) {
            alert(t('upload_error_target_required', "Please select or enter a target name."));
            return;
        }

        setIsUploading(true);
        setOverallProgress(0);
        setCurrentFileIndex(0);
        setCurrentUploadInstructions([]);
        const initialProgress = {};
        selectedFiles.forEach(file => {
            initialProgress[file.name] = { status: 'pending', progress: 0, error: null };
        });
        setUploadProgress(initialProgress);

        try {
            // --- Step 1: Initiate Upload Batch ---
            console.log(`[Upload] Initiating batch upload for target: ${finalTargetName}`);
            setUploadProgress(prev => {
                const nextState = { ...prev };
                Object.keys(nextState).forEach(key => { nextState[key].status = 'initiating'; });
                return nextState;
            });

            const filenames = selectedFiles.map(file => file.name);
            const metadata = {
                focalLength: focalLength,
                exposureTime: exposureTime,
                filterUsed: filterUsed,
                description: selectedTargetOption?.label?.includes('(') ? selectedTargetOption.label.split('(')[1].replace(')', '') : null
            };
            const initiateApiUrl = '/api/uploads';

            const initiateResponse = await fetch(initiateApiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({
                    finalTargetName: finalTargetName,
                    filenames: filenames,
                    metadata: metadata
                })
            });

            if (!initiateResponse.ok) {
                let errorMsg = t('upload_error_initiate_failed', 'Failed to initiate upload batch.');
                try {
                    const errorData = await initiateResponse.json();
                    // TODO: Use t(errorData.errorCode, errorMsg) later
                    errorMsg = errorData.message || errorMsg;
                } catch (e) { /* Ignore */ }
                throw new Error(errorMsg);
            }

            const initiateData = await initiateResponse.json();
            const uploadInstructions = initiateData.uploads;
            setCurrentUploadInstructions(uploadInstructions);

            if (!Array.isArray(uploadInstructions)) {
                throw new Error(t('upload_error_initiate_invalid_response', 'Invalid response received from initiation endpoint.'));
            }
            console.log("[Upload] Received upload instructions:", uploadInstructions);

            // --- Step 2: Upload Files to S3 ---
            for (let i = 0; i < uploadInstructions.length; i++) {
                setCurrentFileIndex(i);
                const instruction = uploadInstructions[i];
                const { originalFilename, presignedUrl, error: urlError } = instruction;
                const fileToUpload = selectedFiles.find(f => f.name === originalFilename);

                if (!fileToUpload || !presignedUrl || urlError) {
                    console.error(`[Upload] Skipping file ${originalFilename}: Missing file object, URL, or URL generation failed. Error: ${urlError || 'Setup error'}`);
                    const skipErrorMsg = urlError || t('upload_error_file_setup_failed', 'Setup failed');
                    setUploadProgress(prev => ({
                        ...prev,
                        [originalFilename]: { ...(prev[originalFilename] || {}), status: 'error', error: skipErrorMsg }
                    }));
                    continue; // Skip to next file
                }

                console.log(`[Upload] Starting PUT to S3 for: ${originalFilename} (File ${i + 1} of ${uploadInstructions.length})`);
                setUploadProgress(prev => ({
                    ...prev,
                    [originalFilename]: { ...(prev[originalFilename] || {}), status: 'uploading', progress: 0 }
                }));

                try {
                    await axios.put(presignedUrl, fileToUpload, {
                        headers: { 'Content-Type': fileToUpload.type },
                        onUploadProgress: (progressEvent) => {
                            const { loaded, total } = progressEvent;
                            const percentCompleted = total ? Math.round((loaded * 100) / total) : 0;

                            setUploadProgress(currentProgressState => {
                                let accumulatedBytes = 0;
                                Object.keys(currentProgressState).forEach(filename => {
                                    const status = currentProgressState[filename]?.status;
                                    const progress = currentProgressState[filename]?.progress || 0;
                                    const fileSize = getFileSize(filename);
                                    if (status === 'success' || (status === 'uploading' && progress === 100)) {
                                        accumulatedBytes += fileSize;
                                    } else if (filename === originalFilename && status === 'uploading') {
                                        accumulatedBytes += loaded;
                                    } else if (status === 'uploading') {
                                        accumulatedBytes += (progress / 100) * fileSize;
                                    }
                                });
                                setOverallProgress(totalBatchSize > 0 ? Math.round((accumulatedBytes * 100) / totalBatchSize) : 0);

                                const currentFileState = currentProgressState[originalFilename] || { status: 'uploading', progress: 0, error: null };
                                return {
                                    ...currentProgressState,
                                    [originalFilename]: { ...currentFileState, progress: percentCompleted }
                                };
                            });
                        } // End onUploadProgress
                    }); // End axios.put

                    console.log(`[Upload] Successfully uploaded: ${originalFilename}`);
                    const uploadedFileSize = getFileSize(originalFilename);

                    setUploadProgress(prev => {
                        const updatedState = {
                            ...prev,
                            [originalFilename]: { ...(prev[originalFilename] || {}), status: 'success', progress: 100 }
                        };

                        // --- NEW: Update Estimated Quota ---
                        if (uploadedFileSize > 0) {
                            // Use functional update based on the PREVIOUS estimate state
                            setEstimatedUsedQuota(currentEstimate =>
                                (currentEstimate !== null ? currentEstimate + uploadedFileSize : uploadedFileSize)
                            );
                        }

                        // Recalculate total bytes accurately after marking complete
                        let accumulatedBytes = 0;
                        Object.keys(updatedState).forEach(filename => {
                            const status = updatedState[filename]?.status;
                            const fileSize = getFileSize(filename);
                            if (status === 'success') { accumulatedBytes += fileSize; }
                        });
                        setOverallProgress(totalBatchSize > 0 ? Math.round((accumulatedBytes * 100) / totalBatchSize) : 0);
                        return updatedState;
                    });
                    // Confirmation call is handled by backend Lambda

                } catch (s3Err) {
                    console.error(`[Upload] S3 Upload failed for ${originalFilename}:`, s3Err.response?.data || s3Err.message || s3Err);
                    const s3ErrorMsg = s3Err.message || t('upload_error_s3_failed', 'S3 upload failed');
                    setUploadProgress(prev => ({
                        ...prev,
                        [originalFilename]: { ...(prev[originalFilename] || {}), status: 'error', error: s3ErrorMsg }
                    }));
                }
            } // End loop

            setCurrentFileIndex(uploadInstructions.length); // Indicate loop finished

        } catch (batchError) {
            console.error("[Upload] Error during upload initiation or setup:", batchError); // Keep technical log

            // 1. Attempt to get specific error code from backend response (like the 401/403 from middleware)
            const errorCode = batchError.response?.data?.errorCode;

            // 2. Determine the localization key to use
            const messageKey = errorCode || 'AUTH_ERROR_GENERIC_REQUEST_FAILED'; // Use specific code or generic key

            // 3. Show user-friendly message using setMessage (NOT alert)
            setMessage(
                t(messageKey, 'Upload initiation failed. Please check selection and try again.') // Use key, provide final fallback text
            );

            // 4. Keep logic to mark pending files as failed in the UI state
            //    (Use the localized message for the error property)
            setUploadProgress(prev => {
                const userFriendlyError = t(messageKey, 'Upload initiation failed.'); // Get translated message again
                const nextState = { ...prev };
                Object.keys(nextState).forEach(key => {
                    if (nextState[key].status === 'pending' || nextState[key].status === 'initiating') {
                        nextState[key] = { ...(nextState[key] || {}), status: 'error', error: userFriendlyError };
                    }
                });
                return nextState;
            });
        } finally {
            setIsUploading(false);
            console.log("[Upload] All file processing finished.");
        }
    }; // End handleUpload


    const customSelectStyles = {
        // Style the dropdown menu container
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#282c34', // Example: Dark background for menu
            border: '1px solid #555',
            zIndex: 100, // Ensure it's on top
        }),
        // Style individual options in the list
        option: (provided, state) => ({
            ...provided,
            // Change background on hover/focus (replace the light blue)
            backgroundColor: state.isFocused ? '#444c56' : 'transparent',
            // Change text color based on focus/selection
            color: state.isFocused || state.isSelected ? '#c9d1d9' : '#9eaab8', // Example light text
            cursor: 'pointer',
            ':active': { // Style when actively clicking
                ...provided[':active'],
                backgroundColor: '#586069',
            },
        }),
        // Style the main input control wrapper
        control: (provided, state) => ({
            ...provided,
            backgroundColor: '#ffffff', // Example dark input background
            borderColor: state.isFocused ? '#1f6feb' : '#30363d', // Border color change on focus
            boxShadow: state.isFocused ? '0 0 0 1px #1f6feb' : provided.boxShadow, // Focus ring
            ':hover': {
                borderColor: state.isFocused ? '#1f6feb' : '#586069',
            }
        }),
        // Style the text you type into the input
        input: (provided) => ({
            ...provided,
            color: '#000000', // Example light input text color
        }),
        // Style the text for the selected value shown in the control
        singleValue: (provided) => ({
            ...provided,
            color: '#000000', // Example light selected text color
        }),
        // Style the placeholder text
        placeholder: (provided) => ({
            ...provided,
            color: '#6e7681', // Example placeholder color
        }),
        // Style the "Creatable..." prompt shown when typing a new option
        creatableOption: (provided) => ({
            ...provided,
            color: '#9eaab8',
        }),
        // Add styles for other parts like indicatorsSeparator, dropdownIndicator etc. if needed
    };
    // *** END custom styles object ***

    // --- Prepare data for the consolidated progress display ---
    const totalFiles = selectedFiles.length;
    const currentInstruction = currentUploadInstructions[currentFileIndex];
    const currentFileNameForDisplay = currentInstruction?.originalFilename || '';
    const currentFileStatusData = uploadProgress[currentFileNameForDisplay] || {};
    const currentFileProgressPercent = currentFileStatusData.progress || 0;
    const currentFileStatusText = currentFileStatusData.status || (currentFileIndex < totalFiles && isUploading ? 'pending' : 'finished');
    const currentFileError = currentFileStatusData.error || null;
    const displayFileNumber = totalFiles > 0 ? Math.min(currentFileIndex + 1, totalFiles) : 0;

    const replacements = useMemo(() => { // Memoize replacements object
        const filenamePart = currentFileNameForDisplay ? ` (${currentFileNameForDisplay})` : '';
        return {
            current: displayFileNumber,
            total: totalFiles,
            filename: currentFileNameForDisplay,
            progress: currentFileProgressPercent,
            // Use t() for the default error text within replacements
            error: currentFileError || t('upload_status_error_default', 'Failed'),
            filename_part: filenamePart // Add filename part for simpler templates
        };
        // Recompute only when these specific values change
    }, [displayFileNumber, totalFiles, currentFileNameForDisplay, currentFileProgressPercent, currentFileError, t]);


    // --- Component Return (JSX) ---
    return (
        <div className="upload-page-container">
            <h1>{t('upload_title', 'Upload Image/Data')}</h1>
            {!user && <p className="upload-user-loading">{t('upload_loading_user_info', 'Loading user info...')}</p>}

            {user && (
                <>
                    {/* --- Metadata Section --- */}
                    <div className="metadata-section">
                        <h3>{t('upload_metadata_title', 'Image Metadata (Applies to all selected files)')}</h3>
                        <div className="metadata-grid">
                            <label htmlFor="target-select">{t('upload_target_label', 'Target:')}</label>
                            {isLoadingTargets ? (
                                <span>{t('upload_loading_targets', 'Loading targets...')}</span>
                            ) : targetError ? (
                                <span className="target-error">{t('upload_target_error_prefix', 'Error loading targets:')} {targetError}</span>
                            ) : (
                                <CreatableSelect
                                    id="target-select"
                                    isClearable
                                    isDisabled={isLoadingTargets || isUploading}
                                    isLoading={isLoadingTargets}
                                    // Pass targetList directly - it already has the value/label format!
                                    options={targetList}
                                    value={selectedTargetOption}
                                    onChange={handleTargetChange}
                                    placeholder={t('upload_target_select_placeholder', "Select existing or type to create...")}
                                    styles={customSelectStyles}
                                />
                            )}
                            <label htmlFor="focalLength">{t('upload_focal_length_label', 'Focal Length (mm):')}</label>
                            <input type="number" id="focalLength" value={focalLength} onChange={e => setFocalLength(e.target.value)} disabled={isUploading} />
                            <label htmlFor="exposureTime">{t('upload_exposure_time_label', 'Exposure Time (s):')}</label>
                            <input type="number" step="any" id="exposureTime" value={exposureTime} onChange={e => setExposureTime(e.target.value)} disabled={isUploading} />
                            <label htmlFor="filterUsed">{t('upload_filter_used_label', 'Filter Used:')}</label>
                            <input type="text" id="filterUsed" value={filterUsed} onChange={e => setFilterUsed(e.target.value)} disabled={isUploading} />
                        </div>
                    </div>

                    {/* File Input Section */}
                    <div className="file-input-section">
                        <label htmlFor="file-upload" className="file-input-label">{t('upload_select_files_label', 'Select Files:')} </label>
                        <input type="file" id="file-upload" multiple onChange={handleFileChange} disabled={isUploading} accept="image/*,.fits,.xisf" />
                    </div>


                    {/* Quota Display */}
                    <div className="quota-info">
                        {/* Display loading/error or the formatted string */}
                        {quotaLoading || quotaError ? (
                            <span className={quotaError ? "error-text" : ""}>{quotaDisplay}</span>
                        ) : (
                            <span>{quotaDisplay}</span>
                        )}
                    </div>

                    {/* Over Quota Warning (Needs updated logic too) */}
                    {isOverQuota && userQuota.allowed !== null && (
                        <p className="error-text">
                            {t('upload_error_quota_exceeded_batch',
                                `Selected files (${formatBytes(totalBatchSize)}) exceed your available storage quota (${formatBytes(userQuota.allowed - userQuota.used)} remaining).`
                                // Using formatBytes here for remaining/selected is probably still okay,
                                // or you could format them consistently too if preferred.
                            )}
                        </p>
                    )}


                    {/* Upload Button */}
                    <button
                        className="upload-button"
                        onClick={handleUpload}
                        disabled={isUploading || selectedFiles.length === 0 || !user || !selectedTargetOption || isOverQuota} // <-- Added isOverQuota
                    >
                        {isUploading ? t('upload_button_loading', 'Uploading...') : t('upload_button_submit', 'Upload Selected Files')}
                    </button>


                    {/* --- ADD THIS BLOCK TO DISPLAY MESSAGES --- */}
                    {/* Conditionally render this paragraph only if 'message' state is not empty */}
                    {message && (
                        <p className={`form-message ${
                            // Basic check to style as error or success based on content
                            // You might want more robust logic later
                            message.toLowerCase().includes('fail') ||
                                message.toLowerCase().includes('error') ||
                                message.toLowerCase().includes('invalid')
                                ? 'error' : 'success'
                            }`}
                        >
                            {message} {/* Display the message state here */}
                        </p>
                    )}
                    {/* --- END MESSAGE DISPLAY BLOCK --- */}


                    {/* --- Progress Display Area --- */}
                    {isUploading && totalFiles > 0 && (
                        <div className="progress-section">
                            <h3>{t('upload_progress_title', 'Upload Status')}</h3>

                            {/* Current File Status using formatString(t(...), replacements) */}
                            <div className="progress-item-current">
                                <span>
                                    {currentFileStatusText === 'uploading' && formatString(t('upload_status_uploading', 'Uploading file {{current}} of {{total}}{{filename_part}} - {{progress}}%'), replacements)}
                                    {currentFileStatusText === 'success' && formatString(t('upload_status_success', 'Uploaded file {{current}} of {{total}}{{filename_part}}'), replacements)}
                                    {currentFileStatusText === 'error' && (<span className="status-error">{formatString(t('upload_status_error', 'Error uploading file {{current}} of {{total}}{{filename_part}}: {{error}}'), replacements)}</span>)}
                                    {(currentFileStatusText === 'pending' || currentFileStatusText === 'initiating') && formatString(t('upload_status_pending', 'Preparing file {{current}} of {{total}}{{filename_part}}...'), replacements)}
                                </span>
                                {(currentFileStatusText === 'uploading' || currentFileStatusText === 'success') && (
                                    <div className="progress-bar-container">
                                        <div className="progress-bar" style={{ width: `${currentFileProgressPercent}%` }}></div>
                                    </div>
                                )}
                            </div>

                            {/* Overall Batch Progress Display */}
                            <div className="progress-item-overall">
                                <span>{t('upload_overall_progress_label', 'Overall Batch Progress')} - {overallProgress}%</span>
                                <div className="progress-bar-container overall-progress-bar-container">
                                    <div className="progress-bar overall-progress-bar" style={{ width: `${overallProgress}%` }}></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Upload Summary Section */}
                    {!isUploading && Object.keys(uploadProgress).length > 0 && currentFileIndex >= totalFiles && (
                        <div className="progress-section summary-section">
                            <h3>{t('upload_summary_title', 'Upload Summary')}</h3>
                            <ul>
                                {selectedFiles.map((file) => {
                                    const statusInfo = uploadProgress[file.name] || { status: 'unknown' };
                                    return (
                                        <li key={file.name}>
                                            {file.name} - <span className={`status-${statusInfo.status}`}>{statusInfo.status}</span> {/* Status text needs localization too eventually */}
                                            {statusInfo.status === 'error' && `: ${statusInfo.error}`}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </>
            )} {/* End user check */}
        </div> // End upload-page-container
    ); // End return
} // End Upload component

export default Upload;



